import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Icon3D = ({ type }: { type: string }): React.ReactElement | null => {
  const icons: Record<string, React.ReactNode> = {
    engineering: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="eng1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="engGlow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <polygon points="40,6 70,23 70,57 40,74 10,57 10,23" fill="none" stroke="url(#eng1)" strokeWidth="2" opacity="0.85" filter="url(#engGlow)"/>
        <polygon points="40,16 60,27 60,53 40,64 20,53 20,27" fill="#4A9EE8" opacity="0.1"/>
        <circle cx="40" cy="40" r="11" fill="none" stroke="#7DBFF5" strokeWidth="2" filter="url(#engGlow)"/>
        <circle cx="40" cy="40" r="5" fill="#7DBFF5" opacity="0.95" filter="url(#engGlow)"/>
        <line x1="40" y1="16" x2="40" y2="29" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.8"/>
        <line x1="40" y1="51" x2="40" y2="64" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.8"/>
        <line x1="20" y1="40" x2="29" y2="40" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.8"/>
        <line x1="51" y1="40" x2="60" y2="40" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.8"/>
        <circle cx="40" cy="6" r="3.5" fill="#7DBFF5" opacity="0.9" filter="url(#engGlow)"/>
        <circle cx="70" cy="23" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="70" cy="57" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="40" cy="74" r="3.5" fill="#7DBFF5" opacity="0.9" filter="url(#engGlow)"/>
        <circle cx="10" cy="57" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="10" cy="23" r="2.5" fill="#4A9EE8" opacity="0.75"/>
      </svg>
    ),
    global: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="gl1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="glGlow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <circle cx="40" cy="40" r="28" fill="none" stroke="url(#gl1)" strokeWidth="2" opacity="0.85" filter="url(#glGlow)"/>
        <circle cx="40" cy="40" r="28" fill="#4A9EE8" opacity="0.05"/>
        <ellipse cx="40" cy="40" rx="13" ry="28" fill="none" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <line x1="12" y1="40" x2="68" y2="40" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <path d="M16 27 Q40 21 64 27" fill="none" stroke="#4A9EE8" strokeWidth="1" opacity="0.4"/>
        <path d="M16 53 Q40 59 64 53" fill="none" stroke="#4A9EE8" strokeWidth="1" opacity="0.4"/>
        <circle cx="40" cy="12" r="3.5" fill="#7DBFF5" filter="url(#glGlow)" opacity="0.95"/>
        <circle cx="40" cy="68" r="3.5" fill="#7DBFF5" filter="url(#glGlow)" opacity="0.95"/>
        <circle cx="68" cy="40" r="3" fill="#4A9EE8" opacity="0.8"/>
        <circle cx="12" cy="40" r="3" fill="#4A9EE8" opacity="0.8"/>
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="sh1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="shGlow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <path d="M40 8 L64 19 L64 43 Q64 60 40 72 Q16 60 16 43 L16 19 Z" fill="#4A9EE8" opacity="0.08"/>
        <path d="M40 8 L64 19 L64 43 Q64 60 40 72 Q16 60 16 43 L16 19 Z" fill="none" stroke="url(#sh1)" strokeWidth="2" opacity="0.9" filter="url(#shGlow)"/>
        <polyline points="29,41 37,49 53,31" fill="none" stroke="#7DBFF5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" filter="url(#shGlow)" opacity="0.95"/>
        <circle cx="40" cy="8" r="3.5" fill="#7DBFF5" filter="url(#shGlow)" opacity="0.95"/>
        <circle cx="64" cy="19" r="2.5" fill="#4A9EE8" opacity="0.75"/>
        <circle cx="16" cy="19" r="2.5" fill="#4A9EE8" opacity="0.75"/>
      </svg>
    ),
    cpu: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="cp1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="cpGlow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="20" y="20" width="40" height="40" rx="5" fill="#4A9EE8" opacity="0.1"/>
        <rect x="20" y="20" width="40" height="40" rx="5" fill="none" stroke="url(#cp1)" strokeWidth="2" opacity="0.9" filter="url(#cpGlow)"/>
        <rect x="29" y="29" width="22" height="22" rx="3" fill="none" stroke="#7DBFF5" strokeWidth="1.5" opacity="0.7"/>
        <line x1="27" y1="6" x2="27" y2="20" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="40" y1="6" x2="40" y2="20" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="53" y1="6" x2="53" y2="20" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="27" y1="60" x2="27" y2="74" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="40" y1="60" x2="40" y2="74" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="53" y1="60" x2="53" y2="74" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="6" y1="27" x2="20" y2="27" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="6" y1="40" x2="20" y2="40" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="6" y1="53" x2="20" y2="53" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="60" y1="27" x2="74" y2="27" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="60" y1="40" x2="74" y2="40" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <line x1="60" y1="53" x2="74" y2="53" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.8"/>
        <circle cx="40" cy="40" r="5" fill="#7DBFF5" filter="url(#cpGlow)" opacity="0.95"/>
      </svg>
    ),
  };
  return (icons[type] as React.ReactElement) || null;
};

const EnergyOrb = () => (
  <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <defs>
      <filter id="orbGlow"><feGaussianBlur stdDeviation="6" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <circle cx="200" cy="200" r="175" fill="none" stroke="#4A9EE8" strokeWidth="0.5" opacity="0.15"/>
    <circle cx="200" cy="200" r="150" fill="none" stroke="#4A9EE8" strokeWidth="0.5" opacity="0.2"/>
    <circle cx="200" cy="200" r="125" fill="none" stroke="#4A9EE8" strokeWidth="1" opacity="0.25"/>
    <circle cx="200" cy="200" r="100" fill="none" stroke="#7DBFF5" strokeWidth="1" opacity="0.35"/>
    <ellipse cx="200" cy="200" rx="130" ry="32" fill="none" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.5" transform="rotate(-30 200 200)"/>
    <ellipse cx="200" cy="200" rx="110" ry="28" fill="none" stroke="#7DBFF5" strokeWidth="1" opacity="0.4" transform="rotate(45 200 200)"/>
    <circle cx="200" cy="200" r="60" fill="#4A9EE8" opacity="0.08"/>
    <circle cx="200" cy="200" r="40" fill="#4A9EE8" opacity="0.15"/>
    <circle cx="200" cy="200" r="25" fill="#2478C8" opacity="0.5"/>
    <circle cx="200" cy="200" r="13" fill="#7DBFF5" opacity="0.9" filter="url(#orbGlow)"/>
    <circle cx="200" cy="200" r="7" fill="#ffffff" opacity="0.95"/>
    <circle cx="330" cy="184" r="5" fill="#7DBFF5" opacity="0.9" filter="url(#orbGlow)"/>
    <circle cx="70" cy="216" r="4" fill="#4A9EE8" opacity="0.8"/>
    <circle cx="248" cy="98" r="4" fill="#7DBFF5" opacity="0.85"/>
    <circle cx="152" cy="302" r="3.5" fill="#4A9EE8" opacity="0.75"/>
    <circle cx="200" cy="200" r="20" fill="#4A9EE8" opacity="0">
      <animate attributeName="r" values="20;55;20" dur="3s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0;0.1;0" dur="3s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

const SectorIcon = ({ type }: { type: string }): React.ReactElement => {
  const map: Record<string, React.ReactElement> = {
    telecom: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs><linearGradient id="s1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient><filter id="sg1"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
        <line x1="40" y1="60" x2="40" y2="20" stroke="url(#s1)" strokeWidth="2.5" opacity="0.85" filter="url(#sg1)"/>
        <path d="M20 38 Q40 20 60 38" fill="none" stroke="#4A9EE8" strokeWidth="2" opacity="0.6"/>
        <path d="M26 46 Q40 30 54 46" fill="none" stroke="#7DBFF5" strokeWidth="1.8" opacity="0.7"/>
        <path d="M32 54 Q40 42 48 54" fill="none" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.5"/>
        <circle cx="40" cy="18" r="4" fill="#7DBFF5" opacity="0.9" filter="url(#sg1)"/>
        <rect x="34" y="58" width="12" height="10" rx="2" fill="#4A9EE8" opacity="0.4"/>
        <circle cx="20" cy="38" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="60" cy="38" r="2.5" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
    industrial: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs><linearGradient id="s2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient><filter id="sg2"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
        <rect x="20" y="20" width="40" height="40" rx="5" fill="none" stroke="url(#s2)" strokeWidth="2" opacity="0.9" filter="url(#sg2)"/>
        <rect x="20" y="20" width="40" height="40" rx="5" fill="#4A9EE8" opacity="0.08"/>
        <rect x="28" y="28" width="10" height="24" rx="2" fill="#4A9EE8" opacity="0.3"/>
        <rect x="42" y="34" width="10" height="18" rx="2" fill="#4A9EE8" opacity="0.3"/>
        <circle cx="33" cy="26" r="3" fill="#7DBFF5" opacity="0.9" filter="url(#sg2)"/>
        <circle cx="47" cy="32" r="3" fill="#4A9EE8" opacity="0.8"/>
        <circle cx="20" cy="20" r="2.5" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="60" cy="20" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="60" cy="60" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="20" cy="60" r="2.5" fill="#7DBFF5" opacity="0.8"/>
      </svg>
    ),
    logistics: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs><linearGradient id="s3" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient><filter id="sg3"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
        <rect x="8" y="28" width="44" height="26" rx="4" fill="none" stroke="url(#s3)" strokeWidth="2" opacity="0.9" filter="url(#sg3)"/>
        <rect x="8" y="28" width="44" height="26" rx="4" fill="#4A9EE8" opacity="0.08"/>
        <path d="M52 36 L64 36 L72 48 L72 54 L52 54 Z" fill="none" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.7"/>
        <circle cx="22" cy="58" r="6" fill="none" stroke="#7DBFF5" strokeWidth="2" filter="url(#sg3)" opacity="0.9"/>
        <circle cx="22" cy="58" r="2.5" fill="#7DBFF5" opacity="0.9"/>
        <circle cx="58" cy="58" r="6" fill="none" stroke="#7DBFF5" strokeWidth="2" filter="url(#sg3)" opacity="0.9"/>
        <circle cx="58" cy="58" r="2.5" fill="#7DBFF5" opacity="0.9"/>
        <line x1="8" y1="36" x2="52" y2="36" stroke="#4A9EE8" strokeWidth="1" opacity="0.4"/>
      </svg>
    ),
    solar: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs><linearGradient id="s4" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient><filter id="sg4"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
        <circle cx="40" cy="40" r="14" fill="none" stroke="url(#s4)" strokeWidth="2" opacity="0.9" filter="url(#sg4)"/>
        <circle cx="40" cy="40" r="14" fill="#4A9EE8" opacity="0.12"/>
        <circle cx="40" cy="40" r="6" fill="#7DBFF5" opacity="0.9" filter="url(#sg4)"/>
        <line x1="40" y1="10" x2="40" y2="20" stroke="#4A9EE8" strokeWidth="2" opacity="0.8"/>
        <line x1="40" y1="60" x2="40" y2="70" stroke="#4A9EE8" strokeWidth="2" opacity="0.8"/>
        <line x1="10" y1="40" x2="20" y2="40" stroke="#4A9EE8" strokeWidth="2" opacity="0.8"/>
        <line x1="60" y1="40" x2="70" y2="40" stroke="#4A9EE8" strokeWidth="2" opacity="0.8"/>
        <line x1="18" y1="18" x2="26" y2="26" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.6"/>
        <line x1="54" y1="54" x2="62" y2="62" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.6"/>
        <line x1="62" y1="18" x2="54" y2="26" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.6"/>
        <line x1="26" y1="54" x2="18" y2="62" stroke="#4A9EE8" strokeWidth="1.5" opacity="0.6"/>
        <circle cx="40" cy="10" r="3" fill="#7DBFF5" opacity="0.9"/>
        <circle cx="70" cy="40" r="3" fill="#4A9EE8" opacity="0.8"/>
        <circle cx="40" cy="70" r="3" fill="#7DBFF5" opacity="0.9"/>
        <circle cx="10" cy="40" r="3" fill="#4A9EE8" opacity="0.8"/>
      </svg>
    ),
    mobility: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs><linearGradient id="s5" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient><filter id="sg5"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
        <rect x="12" y="26" width="56" height="28" rx="8" fill="none" stroke="url(#s5)" strokeWidth="2" opacity="0.9" filter="url(#sg5)"/>
        <rect x="12" y="26" width="56" height="28" rx="8" fill="#4A9EE8" opacity="0.08"/>
        <polygon points="44,30 36,44 42,44 34,52 52,38 46,38 54,30" fill="#7DBFF5" opacity="0.9" filter="url(#sg5)"/>
        <circle cx="24" cy="58" r="7" fill="none" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.7"/>
        <circle cx="24" cy="58" r="3" fill="#4A9EE8" opacity="0.5"/>
        <circle cx="56" cy="58" r="7" fill="none" stroke="#4A9EE8" strokeWidth="1.8" opacity="0.7"/>
        <circle cx="56" cy="58" r="3" fill="#4A9EE8" opacity="0.5"/>
        <circle cx="12" cy="26" r="2.5" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="68" cy="26" r="2.5" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
  };
  return map[type] || map['telecom'];
};

export default function About() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const sectors = [
    { name: t('sector.energy'), svgIcon: 'telecom' },
    { name: t('sector.technology'), svgIcon: 'industrial' },
    { name: t('sector.manufacturing'), svgIcon: 'logistics' },
    { name: t('sector.electronics'), svgIcon: 'solar' },
    { name: t('sector.industrial'), svgIcon: 'mobility' },
  ];

  const cards = [
    { iconType: 'engineering', titleKey: 'about.parentEntity', descKey: 'about.parentEntityDesc', stat: 'End-to-End' },
    { iconType: 'global', titleKey: 'about.uaeBased', descKey: 'about.uaeBasedDesc', stat: 'Dubai, UAE' },
    { iconType: 'shield', titleKey: 'about.activeOwnership', descKey: 'about.activeOwnershipDesc', stat: 'ISO Tested' },
    { iconType: 'cpu', titleKey: 'about.innovationFocus', descKey: 'about.innovationFocusDesc', stat: 'IoT Ready' },
  ];

  return (
    <section id="about" className="py-16 sm:py-32 relative overflow-hidden" style={{ backgroundColor: 'rgba(220,235,255,0.82)' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 relative">

        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 sm:gap-8 mb-10 sm:mb-20 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
          <div className="max-w-2xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold" />
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('about.label')}</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: '#0d2137' }}>
              {t('about.title1')}
              <span className="block" style={{ color: '#4A9EE8' }}>{t('about.title2')}</span>
            </h2>
          </div>
          <p className="text-base sm:text-xl max-w-md leading-relaxed" style={{ color: '#4a5568' }}>{t('about.description')}</p>
        </div>

        {/* Cards — FIXED: stack icon above text on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-10 sm:mb-20">
          {cards.map((card) => (
            <div key={card.titleKey}
              className="group relative p-5 sm:p-10 rounded-2xl border transition-all duration-500 overflow-hidden flex flex-col sm:flex-row gap-5 sm:gap-8 items-start hover:shadow-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.92)', borderColor: 'rgba(74,158,232,0.15)', borderLeft: '4px solid #4A9EE8' }}>
              <div className="absolute top-0 left-0 right-0 h-1 w-0 group-hover:w-full transition-all duration-700" style={{ backgroundColor: '#4A9EE8' }} />
              {/* Icon — smaller on mobile */}
              <div className="w-14 h-14 sm:w-24 sm:h-24 flex-shrink-0">
                <Icon3D type={card.iconType} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs tracking-widest uppercase font-medium px-2 py-1 rounded mb-3 inline-block"
                  style={{ color: '#4A9EE8', border: '1px solid rgba(74,158,232,0.3)', backgroundColor: 'rgba(74,158,232,0.08)' }}>
                  {card.stat}
                </span>
                <h3 className="text-lg sm:text-2xl font-semibold mb-2 sm:mb-3 leading-tight" style={{ color: '#0d2137' }}>
                  {t(card.titleKey)}
                </h3>
                <p className="leading-relaxed text-sm sm:text-base" style={{ color: '#2d3748' }}>
                  {t(card.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Industries */}
        <div className="mb-10 sm:mb-16">
          <div className="text-center mb-8 sm:mb-10">
            <span className="text-lg sm:text-2xl font-thin tracking-[0.2em] sm:tracking-[0.3em] uppercase" style={{ color: '#4A9EE8' }}>{t('about.investmentSectors')}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {sectors.map((sector, idx) => (
              <div key={sector.name}
                className="group relative rounded-2xl p-5 sm:p-8 border transition-all duration-500 overflow-hidden cursor-default text-center hover:shadow-xl"
                style={{ backgroundColor: '#0d2137', borderColor: 'rgba(74,158,232,0.25)' }}>
                <div className="absolute top-0 left-0 right-0 h-1 w-0 group-hover:w-full transition-all duration-700" style={{ backgroundColor: '#4A9EE8' }} />
                <div className="relative">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mb-3 sm:mb-5 mx-auto p-2 sm:p-3"
                    style={{
                      backgroundColor: idx % 2 === 0 ? 'rgba(74,158,232,0.2)' : 'rgba(74,158,232,0.1)',
                      boxShadow: '0 4px 15px rgba(74,158,232,0.2)',
                    }}>
                    <SectorIcon type={sector.svgIcon} />
                  </div>
                  <h4 className="text-sm sm:text-lg leading-tight tracking-wide"
                    style={{ color: '#ffffff', fontFamily: "'Optima', 'Segoe UI', 'Century Gothic', sans-serif", fontWeight: 400, letterSpacing: '0.05em' }}>
                    {sector.name}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA button */}
        <div className="flex justify-center mt-4 mb-12 sm:mb-24">
          <Link to="/about"
            className={`inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-sm sm:text-base rounded-lg transition-all duration-300 group ${isRTL ? 'flex-row-reverse' : ''}`}
            style={{ border: '1px solid rgba(74,158,232,0.4)', color: '#4A9EE8', backgroundColor: '#ffffff' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#4A9EE8'; (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#ffffff'; (e.currentTarget as HTMLElement).style.color = '#4A9EE8'; }}>
            {t('about.learnMore')}
            <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`} />
          </Link>
        </div>

        {/* Quote section */}
        <div className="relative rounded-3xl overflow-hidden border" style={{ backgroundColor: '#0d2137', borderColor: 'rgba(74,158,232,0.2)' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-6 sm:p-12 lg:p-16 flex flex-col justify-center">
              <i className={`ri-double-quotes-${isRTL ? 'r' : 'l'} text-5xl sm:text-7xl block mb-5 sm:mb-8`} style={{ color: 'rgba(74,158,232,0.4)' }} />
              <p className="text-base sm:text-xl lg:text-2xl leading-relaxed font-light tracking-wide" style={{ color: 'rgba(255,255,255,0.9)' }}>
                {t('values.quote2')}
              </p>
              <div className={`flex items-center gap-4 mt-6 sm:mt-10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-px" style={{ backgroundColor: '#4A9EE8' }} />
                <div className="text-sm tracking-widest uppercase" style={{ color: '#4A9EE8' }}>{t('about.alebelteam')}</div>
              </div>
            </div>
            {/* Orb — hidden on mobile to save space */}
            <div className="hidden lg:flex items-center justify-center p-8 lg:p-12">
              <div className="w-80 h-80 lg:w-[420px] lg:h-[420px]" style={{ filter: 'drop-shadow(0 0 40px rgba(74,158,232,0.2))' }}>
                <EnergyOrb />
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
